package org.gymly.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import org.gymly.model.AdminReportKind
import org.gymly.model.AdminStoreSaleMethod

/** Textos de tienda, marketing y reportes. */
interface StoreReportsStrings {
    // ---------- Compartidos por las pantallas del área ----------
    val searchPlaceholder: String
    val view: String
    val addProductButton: String
    val from: String
    val to: String
    val clientLabel: String
    val trainerLabel: String
    val colPrice: String
    val colMethod: String
    val colOrigin: String

    /** Filtro rápido por antigüedad: 7 días / 30 días / todos. */
    fun dateRangeLabel(days: Int?): String

    // ---------- Productos ----------
    val productsTitle: String
    val productsSubtitle: String
    val kpiProducts: String
    val kpiLowStock: String
    val kpiOutOfStock: String
    val kpiActiveProducts: String
    val productCatalog: String
    val noProductsYet: String
    val productsLoadFailed: String
    val addProduct: String
    val editProduct: String
    val deleteProduct: String
    val productCreateFailed: String
    val productSaveFailed: String
    val productDeleteFailed: String
    val colProduct: String
    val colCategory: String
    val colStock: String
    val colActive: String
    val productDetail: String
    val productNameLabel: String
    val productNameRequired: String
    val descriptionLabel: String
    val stockQtyLabel: String
    val descriptionPlaceholder: String
    val activeForSale: String
    val productNameRequiredError: String
    val invalidPrice: String
    val invalidStock: String
    fun confirmDeleteProduct(name: String): String

    // ---------- Marketing ----------
    val marketingTitle: String
    val marketingSubtitle: String
    val kpiTotalPromotions: String
    val kpiActivePromotions: String
    val kpiInactivePromotions: String
    val kpiEndingThisMonth: String
    val promotions: String
    val newPromotionButton: String
    val noPromotionsYet: String
    val promotionsLoadFailed: String
    val addPromotion: String
    val editPromotion: String
    val deletePromotion: String
    val promotionCreateFailed: String
    val promotionSaveFailed: String
    val promotionDeleteFailed: String
    val colPromotion: String
    val colPromotionActive: String
    val promotionDetail: String
    val noBanner: String
    val promotionTitleLabel: String
    val channelLabel: String
    val appFrom: String
    val appTo: String
    val whatsappSend: String
    val whatsappStatus: String
    val noScheduleTime: String
    val marketingChannelRequired: String
    val promotionTitleRequired: String
    val promotionTitlePlaceholder: String
    val bannerRequired: String
    val banner: String
    val noImageSelected: String
    val changeImage: String
    val uploadImage: String
    val bannerHint: String
    val appCarousel: String
    val appCarouselHint: String
    val flexibleDatePlaceholder: String
    val whatsappFlyer: String
    val whatsappFlyerHint: String
    val sendDateRequired: String
    val sendTimeRequired: String
    val sendTimeHint: String
    val promotionTitleRequiredError: String
    val bannerRequiredError: String
    val fromDateInvalid: String
    val toDateInvalid: String
    val dateRangeInvalid: String
    val sendDateRequiredError: String
    val sendTimeInvalid: String
    fun confirmDeletePromotion(title: String): String
    fun imagesCount(count: Int): String
    fun currentImage(count: Int): String

    // ---------- Ventas de la tienda ----------
    val storeSalesTitle: String
    val storeSalesSubtitle: String
    val storeSalesFootnote: String
    val kpiTodaySales: String
    val kpiClientRequests: String
    val kpiPending: String
    val kpiTotalSales: String
    val sales: String
    val registerSaleButton: String
    val noSalesYet: String
    val salesLoadFailed: String
    val registerSale: String
    val editSale: String
    val deleteSale: String
    val saleCreateFailed: String
    val saleSaveFailed: String
    val saleDeleteFailed: String
    val chargeSaleFailed: String
    val cancelSaleFailed: String
    val invoiceDownloadFailed: String
    val saleCreatedProofFailed: String
    val saleSavedProofFailed: String
    val chargeOrCancelSale: String
    val chargeOrCancelHint: String
    val cancelSale: String
    val charge: String
    val colSale: String
    val invoice: String
    val saleDetail: String
    val itemsLabel: String
    val referenceLabel: String
    val colItem: String
    val colQty: String
    val colSubtotal: String
    val downloadPdf: String
    val walkInNoClient: String
    val walkInNameLabel: String
    val selectProduct: String
    val noActiveProducts: String
    val referenceOptional: String
    val referencePlaceholder: String
    val proofLabel: String
    val noFileSelected: String
    val changeProof: String
    val uploadProof: String
    val currentProof: String
    val payLater: String
    val pendingUntilProofVerified: String
    val selectProductInEachRow: String
    val addAtLeastOneItem: String
    val decrease: String
    val increase: String
    val receiptDownloaded: String
    val receiptDownloadFailed: String
    val receiptEmailFailed: String
    val receiptNoClientEmail: String
    fun confirmDeleteSale(number: Long): String
    fun saleCancelled(number: Long): String
    fun saleWithCustomer(number: Long, customer: String): String
    fun methodValue(label: String): String
    fun totalValue(amount: String): String
    fun statusValue(label: String): String
    fun invoiceSaleNumber(number: Long): String
    fun clientValue(name: String): String
    fun dateValue(date: String): String
    fun creditAppliedLine(applied: String, due: String): String
    fun methodsSumMustEqual(amount: String): String
    fun receiptInvoice(number: Long): String
    fun receiptEmailedTo(email: String): String

    /** Equivalente localizado de `AdminStoreSaleMethod.methodHint()`. */
    fun storeSaleMethodHint(method: AdminStoreSaleMethod): String?

    // ---------- Reportes ----------
    val reportsTitle: String
    val reportsSubtitle: String
    val kpiCashIn: String
    val kpiCashOut: String
    val kpiNet: String
    val kpiMovements: String
    val kpiVisits: String
    val kpiClients: String
    val kpiTrainers: String
    val kpiAccessVisits: String
    val kpiMemberships: String
    val kpiActiveMemberships: String
    val kpiRevenue: String
    val kpiSales: String
    val kpiPaidSales: String
    val filters: String
    val dayLabel: String
    val apply: String
    val reset: String
    val typeLabel: String
    val personLabel: String
    val paymentMethodLabel: String
    val allTrainers: String
    val allClients: String
    val allStatuses: String
    val allMethods: String
    val subjectClients: String
    val subjectTrainers: String
    val dateFormatPlaceholder: String
    val salesTrainerFilterHint: String
    val invalidDate: String
    val invalidDates: String
    val reportLoadFailed: String
    val reportExportFailed: String
    val export: String
    val noDataForFilters: String
    val noMovementsForDay: String
    val totalsByMethod: String
    val grandTotal: String
    val totals: String
    val colTime: String
    val colScheduleClass: String
    val colItems: String
    val colPlan: String
    val colStart: String
    val colDetail: String

    /** Pestaña del selector de reporte. */
    fun reportKindTab(kind: AdminReportKind): String

    /** Título de la tarjeta de detalle de cada reporte. */
    fun reportDetailTitle(kind: AdminReportKind): String
    fun methodWithCount(label: String, count: Int): String
    fun totalForMethod(label: String, count: Int): String
}

internal object StoreReportsEs : StoreReportsStrings {
    // ---------- Compartidos por las pantallas del área ----------
    override val searchPlaceholder = "Buscar..."
    override val view = "Ver"
    override val addProductButton = "+ Agregar producto"
    override val from = "Desde"
    override val to = "Hasta"
    override val clientLabel = "Cliente"
    override val trainerLabel = "Entrenador"
    override val colPrice = "Precio"
    override val colMethod = "Método"
    override val colOrigin = "Origen"

    override fun dateRangeLabel(days: Int?) = when (days) {
        null -> "Todos"
        else -> "$days días"
    }

    // ---------- Productos ----------
    override val productsTitle = "Productos"
    override val productsSubtitle =
        "Mercancía, equipamiento y alimentos/suplementos que se venden en el gimnasio."
    override val kpiProducts = "Productos"
    override val kpiLowStock = "Poco stock"
    override val kpiOutOfStock = "Agotado"
    override val kpiActiveProducts = "Activos"
    override val productCatalog = "Catálogo de productos"
    override val noProductsYet = "No hay productos todavía."
    override val productsLoadFailed = "No se pudieron cargar los productos."
    override val addProduct = "Agregar producto"
    override val editProduct = "Editar producto"
    override val deleteProduct = "Eliminar producto"
    override val productCreateFailed = "No se pudo crear el producto."
    override val productSaveFailed = "No se pudo guardar el producto."
    override val productDeleteFailed = "No se pudo eliminar."
    override val colProduct = "Producto"
    override val colCategory = "Categoría"
    override val colStock = "Stock"
    override val colActive = "Activo"
    override val productDetail = "Detalle del producto"
    override val productNameLabel = "Nombre del producto"
    override val productNameRequired = "Nombre del producto *"
    override val descriptionLabel = "Descripción"
    override val stockQtyLabel = "Cantidad en stock"
    override val descriptionPlaceholder = "Detalles, materiales, tallas, ingredientes..."
    override val activeForSale = "Activo (disponible para la venta)"
    override val productNameRequiredError = "El nombre es obligatorio."
    override val invalidPrice = "Precio inválido."
    override val invalidStock = "Stock inválido."
    override fun confirmDeleteProduct(name: String) =
        "¿Eliminar '$name'? Se quitará del catálogo de la tienda."

    // ---------- Marketing ----------
    override val marketingTitle = "Marketing"
    override val marketingSubtitle =
        "Carrusel de la app / web móvil y flyers programados por WhatsApp. " +
            "Elige el canal al crear cada pieza."
    override val kpiTotalPromotions = "Total de promociones"
    override val kpiActivePromotions = "Activas"
    override val kpiInactivePromotions = "Inactivas"
    override val kpiEndingThisMonth = "Terminan este mes"
    override val promotions = "Promociones"
    override val newPromotionButton = "+ Nueva promoción"
    override val noPromotionsYet = "No hay promociones todavía."
    override val promotionsLoadFailed = "No se pudieron cargar las promociones."
    override val addPromotion = "Agregar promoción"
    override val editPromotion = "Editar promoción"
    override val deletePromotion = "Eliminar promoción"
    override val promotionCreateFailed = "No se pudo crear la promoción."
    override val promotionSaveFailed = "No se pudo guardar la promoción."
    override val promotionDeleteFailed = "No se pudo eliminar la promoción."
    override val colPromotion = "Promoción"
    override val colPromotionActive = "Activa"
    override val promotionDetail = "Detalle de la promoción"
    override val noBanner = "Sin banner"
    override val promotionTitleLabel = "Título"
    override val channelLabel = "Canal"
    override val appFrom = "Desde (app)"
    override val appTo = "Hasta (app)"
    override val whatsappSend = "Envío WhatsApp"
    override val whatsappStatus = "Estado WhatsApp"
    override val noScheduleTime = "Sin hora"
    override val marketingChannelRequired = "Canal de marketing *"
    override val promotionTitleRequired = "Título *"
    override val promotionTitlePlaceholder = "2x1 en plan mensual"
    override val bannerRequired = "Banner / flyer *"
    override val banner = "Banner / flyer"
    override val noImageSelected = "No se seleccionó ninguna imagen."
    override val changeImage = "Cambiar imagen"
    override val uploadImage = "Subir imagen"
    override val bannerHint = "Recomendado 1200×600. Misma imagen para app y/o WhatsApp."
    override val appCarousel = "App (carrusel)"
    override val appCarouselHint = "Se muestra en la app y web móvil entre estas fechas."
    override val flexibleDatePlaceholder = "AAAA-MM-DD o DD/MM/AAAA"
    override val whatsappFlyer = "WhatsApp (flyer)"
    override val whatsappFlyerHint =
        "Se envía con el WhatsApp configurado en Ajustes. Indica cuándo programar el envío."
    override val sendDateRequired = "Fecha de envío *"
    override val sendTimeRequired = "Hora de envío *"
    override val sendTimeHint = "Formato 24 h (HH:mm). Zona America/Caracas (−04:00)."
    override val promotionTitleRequiredError = "El título es obligatorio."
    override val bannerRequiredError = "Sube el banner / flyer (recomendado 1200×600)."
    override val fromDateInvalid = "Desde: usa DD/MM/AAAA o AAAA-MM-DD."
    override val toDateInvalid = "Hasta: usa DD/MM/AAAA o AAAA-MM-DD."
    override val dateRangeInvalid = "La fecha Hasta debe ser posterior o igual a Desde."
    override val sendDateRequiredError = "Indica la fecha de envío por WhatsApp."
    override val sendTimeInvalid = "Hora de envío: usa HH:mm (ej. 18:00)."
    override fun confirmDeletePromotion(title: String) =
        "¿Seguro que deseas eliminar «$title»? Esta acción no se puede deshacer."
    override fun imagesCount(count: Int) = "$count imagen(es)"
    override fun currentImage(count: Int) = "Imagen actual ($count)"

    // ---------- Ventas de la tienda ----------
    override val storeSalesTitle = "Ventas de la tienda"
    override val storeSalesSubtitle =
        "Solicitudes de compra de clientes y ventas registradas por el personal — " +
            "POP, equipamiento y alimentos/suplementos."
    override val storeSalesFootnote =
        "Las compras por pago móvil, transferencia y Zelle las aprueba el personal tras " +
            "verificar el comprobante adjunto. Tarjeta y PayPal se liquidan automáticamente " +
            "vía Stripe/PayPal."
    override val kpiTodaySales = "Ventas de hoy"
    override val kpiClientRequests = "Solicitudes de clientes"
    override val kpiPending = "Pendientes"
    override val kpiTotalSales = "Total de ventas"
    override val sales = "Ventas"
    override val registerSaleButton = "+ Registrar venta"
    override val noSalesYet = "No hay ventas todavía."
    override val salesLoadFailed = "No se pudieron cargar las ventas."
    override val registerSale = "Registrar venta"
    override val editSale = "Editar venta"
    override val deleteSale = "Eliminar venta"
    override val saleCreateFailed = "No se pudo registrar la venta."
    override val saleSaveFailed = "No se pudo guardar la venta."
    override val saleDeleteFailed = "No se pudo eliminar la venta."
    override val chargeSaleFailed = "No se pudo cobrar la venta."
    override val cancelSaleFailed = "No se pudo cancelar la venta."
    override val invoiceDownloadFailed = "No se pudo descargar la factura."
    override val saleCreatedProofFailed = "La venta se creó pero el comprobante no quedó guardado."
    override val saleSavedProofFailed = "Los datos se guardaron pero el comprobante no."
    override val chargeOrCancelSale = "Cobrar o cancelar venta"
    override val chargeOrCancelHint = "Confirma el cobro o cancela la venta pendiente."
    override val cancelSale = "Cancelar venta"
    override val charge = "Cobrar"
    override val colSale = "Venta"
    override val invoice = "Factura"
    override val saleDetail = "Detalle de la venta"
    override val itemsLabel = "Artículos"
    override val referenceLabel = "Referencia"
    override val colItem = "Artículo"
    override val colQty = "Cant."
    override val colSubtotal = "Subtotal"
    override val downloadPdf = "Descargar PDF"
    override val walkInNoClient = "Cliente Externo"
    override val walkInNameLabel = "Nombre cliente externo"
    override val selectProduct = "Seleccionar producto"
    override val noActiveProducts = "No hay productos activos"
    override val referenceOptional = "Referencia (opcional)"
    override val referencePlaceholder = "Nº de referencia / operación"
    override val proofLabel = "Comprobante (imagen o PDF)"
    override val noFileSelected = "No se seleccionó ningún archivo."
    override val changeProof = "Cambiar comprobante"
    override val uploadProof = "Subir comprobante (imagen o PDF)"
    override val currentProof = "Comprobante actual"
    override val payLater = "Pagar más tarde"
    override val pendingUntilProofVerified =
        "Este método queda pendiente hasta verificar el comprobante."
    override val selectProductInEachRow =
        "Selecciona un producto en cada fila (o quita las vacías)."
    override val addAtLeastOneItem = "Agrega al menos un artículo."
    override val decrease = "Disminuir"
    override val increase = "Aumentar"
    override val receiptDownloaded = " · descargada"
    override val receiptDownloadFailed = " · no se pudo descargar"
    override val receiptEmailFailed = " · mail no enviado"
    override val receiptNoClientEmail = " · sin correo de cliente"
    override fun confirmDeleteSale(number: Long) =
        "¿Eliminar la venta #$number? Esto no se puede deshacer."
    override fun saleCancelled(number: Long) = "Venta #$number cancelada."
    override fun saleWithCustomer(number: Long, customer: String) = "Venta #$number · $customer"
    override fun methodValue(label: String) = "Método: $label"
    override fun totalValue(amount: String) = "Total: $amount"
    override fun statusValue(label: String) = "Estado: $label"
    override fun invoiceSaleNumber(number: Long) = "Factura · Venta #$number"
    override fun clientValue(name: String) = "Cliente: $name"
    override fun dateValue(date: String) = "Fecha: $date"
    override fun creditAppliedLine(applied: String, due: String) =
        "Saldo aplicado: $applied · Por cobrar: $due"
    override fun methodsSumMustEqual(amount: String) =
        "La suma de métodos debe igualar $amount."
    override fun receiptInvoice(number: Long) = "Factura #$number"
    override fun receiptEmailedTo(email: String) = " · mail a $email"

    override fun storeSaleMethodHint(method: AdminStoreSaleMethod): String? = when (method) {
        AdminStoreSaleMethod.CARD -> "Los pagos con tarjeta se procesan vía Stripe."
        AdminStoreSaleMethod.PAYPAL -> "Los pagos con PayPal se liquidan automáticamente."
        AdminStoreSaleMethod.DIVISAS ->
            "Cobro en dólares (USD) en recepción. Queda marcado como pagado al guardar."
        AdminStoreSaleMethod.USDT ->
            "Cobro en dólares (USD) vía USDT. Indica el monto en $; no se suma a Total Bs."
        AdminStoreSaleMethod.MOBILE_PAYMENT,
        AdminStoreSaleMethod.TRANSFER,
        AdminStoreSaleMethod.ZELLE,
        -> "Puedes adjuntar referencia y/o imagen del comprobante."
        AdminStoreSaleMethod.CASH -> null
    }

    // ---------- Reportes ----------
    override val reportsTitle = "Reportes"
    override val reportsSubtitle = "Filtros, detalle paginado y exportación PDF/Excel."
    override val kpiCashIn = "Entradas"
    override val kpiCashOut = "Vuelto"
    override val kpiNet = "Neto"
    override val kpiMovements = "Movimientos"
    override val kpiVisits = "Visitas"
    override val kpiClients = "Clientes"
    override val kpiTrainers = "Entrenadores"
    override val kpiAccessVisits = "Por acceso"
    override val kpiMemberships = "Membresías"
    override val kpiActiveMemberships = "Activas"
    override val kpiRevenue = "Ingresos"
    override val kpiSales = "Ventas"
    override val kpiPaidSales = "Pagadas"
    override val filters = "Filtros"
    override val dayLabel = "Día"
    override val apply = "Aplicar"
    override val reset = "Restablecer"
    override val typeLabel = "Tipo"
    override val personLabel = "Persona"
    override val paymentMethodLabel = "Método de pago"
    override val allTrainers = "Todos los entrenadores"
    override val allClients = "Todos los clientes"
    override val allStatuses = "Todos los estados"
    override val allMethods = "Todos los métodos"
    override val subjectClients = "Clientes"
    override val subjectTrainers = "Entrenadores"
    override val dateFormatPlaceholder = "DD/MM/AAAA"
    override val salesTrainerFilterHint =
        "En ventas, el filtro de entrenador usa la membresía activa del cliente."
    override val invalidDate = "Usa una fecha válida (DD/MM/AAAA o AAAA-MM-DD)."
    override val invalidDates = "Usa fechas válidas (DD/MM/AAAA o AAAA-MM-DD)."
    override val reportLoadFailed = "No se pudo cargar el reporte."
    override val reportExportFailed = "No se pudo exportar el reporte."
    override val export = "Exportar"
    override val noDataForFilters = "Sin datos para los filtros seleccionados."
    override val noMovementsForDay = "Sin movimientos para el día seleccionado."
    override val totalsByMethod = "Totales por método"
    override val grandTotal = "Suma total"
    override val totals = "Totales"
    override val colTime = "Hora"
    override val colScheduleClass = "Clase/Horario"
    override val colItems = "Ítems"
    override val colPlan = "Plan"
    override val colStart = "Inicio"
    override val colDetail = "Detalle"

    override fun reportKindTab(kind: AdminReportKind) = when (kind) {
        AdminReportKind.CASH_RECONCILIATION -> "Cuadre de caja"
        AdminReportKind.SESSIONS -> "Asistencias"
        AdminReportKind.MEMBERSHIPS -> "Membresías"
        AdminReportKind.ENROLLMENTS -> "Inscritos"
        AdminReportKind.SALES -> "Ventas"
        AdminReportKind.INVENTORY -> "Inventario"
    }

    override fun reportDetailTitle(kind: AdminReportKind) = when (kind) {
        AdminReportKind.CASH_RECONCILIATION -> "Detalle del día"
        AdminReportKind.SESSIONS -> "Detalle de asistencias"
        AdminReportKind.MEMBERSHIPS -> "Detalle de membresías"
        AdminReportKind.ENROLLMENTS -> "Detalle de inscritos"
        AdminReportKind.SALES -> "Detalle de ventas"
        AdminReportKind.INVENTORY -> "Detalle de inventario"
    }

    override fun methodWithCount(label: String, count: Int) = "$label ($count)"
    override fun totalForMethod(label: String, count: Int) = "Total $label ($count)"
}

internal object StoreReportsEn : StoreReportsStrings {
    // ---------- Compartidos por las pantallas del área ----------
    override val searchPlaceholder = "Search..."
    override val view = "View"
    override val addProductButton = "+ Add product"
    override val from = "From"
    override val to = "To"
    override val clientLabel = "Client"
    override val trainerLabel = "Trainer"
    override val colPrice = "Price"
    override val colMethod = "Method"
    override val colOrigin = "Origin"

    override fun dateRangeLabel(days: Int?) = when (days) {
        null -> "All time"
        1 -> "Last day"
        else -> "Last $days days"
    }

    // ---------- Productos ----------
    override val productsTitle = "Products"
    override val productsSubtitle =
        "Merch, equipment and food/supplements sold at the gym."
    override val kpiProducts = "Products"
    override val kpiLowStock = "Low stock"
    override val kpiOutOfStock = "Out of stock"
    override val kpiActiveProducts = "Active"
    override val productCatalog = "Product catalog"
    override val noProductsYet = "No products yet."
    override val productsLoadFailed = "Could not load the products."
    override val addProduct = "Add product"
    override val editProduct = "Edit product"
    override val deleteProduct = "Delete product"
    override val productCreateFailed = "Could not create the product."
    override val productSaveFailed = "Could not save the product."
    override val productDeleteFailed = "Could not delete it."
    override val colProduct = "Product"
    override val colCategory = "Category"
    override val colStock = "Stock"
    override val colActive = "Active"
    override val productDetail = "Product details"
    override val productNameLabel = "Product name"
    override val productNameRequired = "Product name *"
    override val descriptionLabel = "Description"
    override val stockQtyLabel = "Stock quantity"
    override val descriptionPlaceholder = "Details, materials, sizes, ingredients..."
    override val activeForSale = "Active (available for sale)"
    override val productNameRequiredError = "Name is required."
    override val invalidPrice = "Invalid price."
    override val invalidStock = "Invalid stock."
    override fun confirmDeleteProduct(name: String) =
        "Delete '$name'? It will be removed from the store catalog."

    // ---------- Marketing ----------
    override val marketingTitle = "Marketing"
    override val marketingSubtitle =
        "App / mobile web carousel and flyers scheduled over WhatsApp. " +
            "Pick the channel when you create each piece."
    override val kpiTotalPromotions = "Total promotions"
    override val kpiActivePromotions = "Active"
    override val kpiInactivePromotions = "Inactive"
    override val kpiEndingThisMonth = "Ending this month"
    override val promotions = "Promotions"
    override val newPromotionButton = "+ New promotion"
    override val noPromotionsYet = "No promotions yet."
    override val promotionsLoadFailed = "Could not load the promotions."
    override val addPromotion = "Add promotion"
    override val editPromotion = "Edit promotion"
    override val deletePromotion = "Delete promotion"
    override val promotionCreateFailed = "Could not create the promotion."
    override val promotionSaveFailed = "Could not save the promotion."
    override val promotionDeleteFailed = "Could not delete the promotion."
    override val colPromotion = "Promotion"
    override val colPromotionActive = "Active"
    override val promotionDetail = "Promotion details"
    override val noBanner = "No banner"
    override val promotionTitleLabel = "Title"
    override val channelLabel = "Channel"
    override val appFrom = "From (app)"
    override val appTo = "To (app)"
    override val whatsappSend = "WhatsApp delivery"
    override val whatsappStatus = "WhatsApp status"
    override val noScheduleTime = "No time set"
    override val marketingChannelRequired = "Marketing channel *"
    override val promotionTitleRequired = "Title *"
    override val promotionTitlePlaceholder = "2-for-1 on the monthly plan"
    override val bannerRequired = "Banner / flyer *"
    override val banner = "Banner / flyer"
    override val noImageSelected = "No image was selected."
    override val changeImage = "Change image"
    override val uploadImage = "Upload image"
    override val bannerHint = "1200×600 recommended. Same image for the app and/or WhatsApp."
    override val appCarousel = "App (carousel)"
    override val appCarouselHint = "Shown in the app and mobile web between these dates."
    override val flexibleDatePlaceholder = "YYYY-MM-DD or DD/MM/YYYY"
    override val whatsappFlyer = "WhatsApp (flyer)"
    override val whatsappFlyerHint =
        "Sent from the WhatsApp number configured in Settings. Choose when to schedule delivery."
    override val sendDateRequired = "Delivery date *"
    override val sendTimeRequired = "Delivery time *"
    override val sendTimeHint = "24-hour format (HH:mm). America/Caracas time zone (−04:00)."
    override val promotionTitleRequiredError = "Title is required."
    override val bannerRequiredError = "Upload the banner / flyer (1200×600 recommended)."
    override val fromDateInvalid = "From: use DD/MM/YYYY or YYYY-MM-DD."
    override val toDateInvalid = "To: use DD/MM/YYYY or YYYY-MM-DD."
    override val dateRangeInvalid = "The To date must be the same as or later than the From date."
    override val sendDateRequiredError = "Choose the WhatsApp delivery date."
    override val sendTimeInvalid = "Delivery time: use HH:mm (e.g. 18:00)."
    override fun confirmDeletePromotion(title: String) =
        "Are you sure you want to delete “$title”? This action cannot be undone."
    override fun imagesCount(count: Int) = if (count == 1) "1 image" else "$count images"
    override fun currentImage(count: Int) = "Current image ($count)"

    // ---------- Ventas de la tienda ----------
    override val storeSalesTitle = "Store sales"
    override val storeSalesSubtitle =
        "Client purchase requests and sales registered by staff — " +
            "merch, equipment and food/supplements."
    override val storeSalesFootnote =
        "Mobile payment, bank transfer and Zelle purchases are approved by staff after " +
            "checking the attached receipt. Card and PayPal settle automatically through " +
            "Stripe/PayPal."
    override val kpiTodaySales = "Today's sales"
    override val kpiClientRequests = "Client requests"
    override val kpiPending = "Pending"
    override val kpiTotalSales = "Total sales"
    override val sales = "Sales"
    override val registerSaleButton = "+ Register sale"
    override val noSalesYet = "No sales yet."
    override val salesLoadFailed = "Could not load the sales."
    override val registerSale = "Register sale"
    override val editSale = "Edit sale"
    override val deleteSale = "Delete sale"
    override val saleCreateFailed = "Could not register the sale."
    override val saleSaveFailed = "Could not save the sale."
    override val saleDeleteFailed = "Could not delete the sale."
    override val chargeSaleFailed = "Could not charge the sale."
    override val cancelSaleFailed = "Could not cancel the sale."
    override val invoiceDownloadFailed = "Could not download the invoice."
    override val saleCreatedProofFailed = "The sale was created but the receipt was not saved."
    override val saleSavedProofFailed = "The details were saved but the receipt was not."
    override val chargeOrCancelSale = "Charge or cancel sale"
    override val chargeOrCancelHint = "Confirm the payment or cancel the pending sale."
    override val cancelSale = "Cancel sale"
    override val charge = "Charge"
    override val colSale = "Sale"
    override val invoice = "Invoice"
    override val saleDetail = "Sale details"
    override val itemsLabel = "Items"
    override val referenceLabel = "Reference"
    override val colItem = "Item"
    override val colQty = "Qty"
    override val colSubtotal = "Subtotal"
    override val downloadPdf = "Download PDF"
    override val walkInNoClient = "External customer"
    override val walkInNameLabel = "External customer name"
    override val selectProduct = "Select product"
    override val noActiveProducts = "No active products"
    override val referenceOptional = "Reference (optional)"
    override val referencePlaceholder = "Reference / transaction no."
    override val proofLabel = "Receipt (image or PDF)"
    override val noFileSelected = "No file was selected."
    override val changeProof = "Change receipt"
    override val uploadProof = "Upload receipt (image or PDF)"
    override val currentProof = "Current receipt"
    override val payLater = "Pay later"
    override val pendingUntilProofVerified =
        "This method stays pending until the receipt is verified."
    override val selectProductInEachRow =
        "Select a product in every row (or remove the empty ones)."
    override val addAtLeastOneItem = "Add at least one item."
    override val decrease = "Decrease"
    override val increase = "Increase"
    override val receiptDownloaded = " · downloaded"
    override val receiptDownloadFailed = " · could not be downloaded"
    override val receiptEmailFailed = " · email not sent"
    override val receiptNoClientEmail = " · no client email"
    override fun confirmDeleteSale(number: Long) =
        "Delete sale #$number? This cannot be undone."
    override fun saleCancelled(number: Long) = "Sale #$number cancelled."
    override fun saleWithCustomer(number: Long, customer: String) = "Sale #$number · $customer"
    override fun methodValue(label: String) = "Method: $label"
    override fun totalValue(amount: String) = "Total: $amount"
    override fun statusValue(label: String) = "Status: $label"
    override fun invoiceSaleNumber(number: Long) = "Invoice · Sale #$number"
    override fun clientValue(name: String) = "Client: $name"
    override fun dateValue(date: String) = "Date: $date"
    override fun creditAppliedLine(applied: String, due: String) =
        "Credit applied: $applied · Amount due: $due"
    override fun methodsSumMustEqual(amount: String) =
        "The payment methods must add up to $amount."
    override fun receiptInvoice(number: Long) = "Invoice #$number"
    override fun receiptEmailedTo(email: String) = " · emailed to $email"

    override fun storeSaleMethodHint(method: AdminStoreSaleMethod): String? = when (method) {
        AdminStoreSaleMethod.CARD -> "Card payments are processed through Stripe."
        AdminStoreSaleMethod.PAYPAL -> "PayPal payments settle automatically."
        AdminStoreSaleMethod.DIVISAS ->
            "USD cash received at the counter. Marked paid on save."
        AdminStoreSaleMethod.USDT ->
            "USD via USDT. Enter the $ amount; it is not added to Total Bs."
        AdminStoreSaleMethod.MOBILE_PAYMENT,
        AdminStoreSaleMethod.TRANSFER,
        AdminStoreSaleMethod.ZELLE,
        -> "You can attach a reference and/or a photo of the receipt."
        AdminStoreSaleMethod.CASH -> null
    }

    // ---------- Reportes ----------
    override val reportsTitle = "Reports"
    override val reportsSubtitle = "Filters, paginated detail and PDF/Excel export."
    override val kpiCashIn = "Cash in"
    override val kpiCashOut = "Change given"
    override val kpiNet = "Net"
    override val kpiMovements = "Movements"
    override val kpiVisits = "Visits"
    override val kpiClients = "Clients"
    override val kpiTrainers = "Trainers"
    override val kpiAccessVisits = "By access"
    override val kpiMemberships = "Memberships"
    override val kpiActiveMemberships = "Active"
    override val kpiRevenue = "Revenue"
    override val kpiSales = "Sales"
    override val kpiPaidSales = "Paid"
    override val filters = "Filters"
    override val dayLabel = "Day"
    override val apply = "Apply"
    override val reset = "Reset"
    override val typeLabel = "Type"
    override val personLabel = "Person"
    override val paymentMethodLabel = "Payment method"
    override val allTrainers = "All trainers"
    override val allClients = "All clients"
    override val allStatuses = "All statuses"
    override val allMethods = "All methods"
    override val subjectClients = "Clients"
    override val subjectTrainers = "Trainers"
    override val dateFormatPlaceholder = "DD/MM/YYYY"
    override val salesTrainerFilterHint =
        "For sales, the trainer filter uses the client's active membership."
    override val invalidDate = "Use a valid date (DD/MM/YYYY or YYYY-MM-DD)."
    override val invalidDates = "Use valid dates (DD/MM/YYYY or YYYY-MM-DD)."
    override val reportLoadFailed = "Could not load the report."
    override val reportExportFailed = "Could not export the report."
    override val export = "Export"
    override val noDataForFilters = "No data for the selected filters."
    override val noMovementsForDay = "No movements for the selected day."
    override val totalsByMethod = "Totals by method"
    override val grandTotal = "Grand total"
    override val totals = "Totals"
    override val colTime = "Time"
    override val colScheduleClass = "Class/Schedule"
    override val colItems = "Items"
    override val colPlan = "Plan"
    override val colStart = "Start"
    override val colDetail = "Detail"

    override fun reportKindTab(kind: AdminReportKind) = when (kind) {
        AdminReportKind.CASH_RECONCILIATION -> "Cash reconciliation"
        AdminReportKind.SESSIONS -> "Attendance"
        AdminReportKind.MEMBERSHIPS -> "Memberships"
        AdminReportKind.ENROLLMENTS -> "Enrollments"
        AdminReportKind.SALES -> "Sales"
        AdminReportKind.INVENTORY -> "Inventory"
    }

    override fun reportDetailTitle(kind: AdminReportKind) = when (kind) {
        AdminReportKind.CASH_RECONCILIATION -> "Day detail"
        AdminReportKind.SESSIONS -> "Attendance detail"
        AdminReportKind.MEMBERSHIPS -> "Membership detail"
        AdminReportKind.ENROLLMENTS -> "Enrollment detail"
        AdminReportKind.SALES -> "Sales detail"
        AdminReportKind.INVENTORY -> "Inventory detail"
    }

    override fun methodWithCount(label: String, count: Int) = "$label ($count)"
    override fun totalForMethod(label: String, count: Int) = "$label total ($count)"
}

@Composable
@ReadOnlyComposable
fun storeReportsStrings(): StoreReportsStrings = pickStrings(StoreReportsEs, StoreReportsEn)
